import { Truck, VendorMaterialStock } from '@/types/app';
import { VolumeAndWeightUnits } from '@/enums';
import { zeroDecimals } from '@/constants';

const formatNum = (value: number | string): string => {
  if (typeof value === 'number') {
    return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  return value;
};

const formatConversion = (value: number | string): string => {
  if (typeof value === 'number') {
    return value.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
  }
  return value;
};

const roundNumber = (value: number): number => {
  return Math.round(value * 100) / 100;
};

const getMaterialCost = (
  selectedMaterialStock: VendorMaterialStock | undefined,
  quantityMetric: VolumeAndWeightUnits,
  quantity: number,
  conversionRate: { value: number }
) => {
  let value = selectedMaterialStock?.rate.number ?? 0;
  let convertedValue = 0;
  let isSameUnit = false;
  if (selectedMaterialStock?.rate.metric == quantityMetric) {
    isSameUnit = true;
  } else if (quantityMetric == 'cy') {
    convertedValue = value * conversionRate.value;
  } else {
    convertedValue = value / conversionRate.value;
  }

  if (!isSameUnit) {
    value = convertedValue;
  }

  value = roundNumber(value);

  return {
    value,
    get formatted() {
      return formatNum(this.value);
    },
    get tooltip() {
      let baseTooltip = `$${selectedMaterialStock?.rate.number} / ${selectedMaterialStock?.rate.metric} (vendor settings Material Cost)`;

      if (!isSameUnit) {
        if (quantityMetric === 't') {
          baseTooltip += `\n/ ${conversionRate.value} (cy -> t conversion rate)`;
        } else if (quantityMetric === 'cy') {
          baseTooltip += `\n* ${conversionRate.value} (t -> cy conversion rate)`;
        }
        baseTooltip += `\n$${formatNum(value)} / ${quantityMetric} [Material Cost (calculated price / ${quantityMetric})]`;
      }

      return baseTooltip;
    }
  };
};

const getMarkup = (
  quantity: number,
  markup: number | undefined,
  quantityMetric: VolumeAndWeightUnits,
  doApplyMarkup: boolean
) => {
  return {
    value: doApplyMarkup ? quantity * (markup ?? 0) : 0,
    get formatted() {
      return formatNum(this.value);
    },
    get tooltip() {
      return (
        `${quantity} ${quantityMetric} [Qty]\n` +
        `x $${doApplyMarkup ? markup : 0} / ${quantityMetric} (admin settings Mark Up)\n` +
        `= $${this.formatted} [Mark Up]\n`
      );
    }
  };
};

const getSubtotalCost = (
  materialCost: { value: number; formatted: string },
  quantity: number,
  quantityMetric: VolumeAndWeightUnits,
  selectedMaterialStock: VendorMaterialStock | undefined,
  conversionRate: { value: number },
  markup: number | undefined,
  doApplyMarkup: boolean
) => {
  const markupObj = getMarkup(quantity, markup, quantityMetric, doApplyMarkup);
  const stockRate = selectedMaterialStock?.rate.number ?? 0;
  let value = 0;
  let isSameUnit = false;
  if (selectedMaterialStock?.rate.metric == quantityMetric) {
    value = stockRate * quantity;
    isSameUnit = true;
  } else if (quantityMetric == 'cy') {
    value = stockRate * conversionRate.value * quantity;
  } else {
    value = (stockRate / conversionRate.value) * quantity;
  }

  if (markupObj.value !== 0) {
    value += markupObj.value;
  }

  value = roundNumber(value);

  return {
    value,
    get formatted() {
      return formatNum(this.value);
    },
    get tooltip() {
      return (
        `${quantity} ${quantityMetric} [Qty]\n` +
        `${
          isSameUnit
            ? ''
            : `\n**Material is sold in ${selectedMaterialStock?.rate.metric} (but requested in ${quantityMetric}), so we need to convert qty\n` +
              `${formatConversion(
                selectedMaterialStock?.rate.metric === 't'
                  ? quantity * conversionRate.value
                  : quantity * (1 / conversionRate.value)
              )} ${selectedMaterialStock?.rate.metric} [Qty converted (cy->t = ${conversionRate.value})]\n`
        }` +
        `x $${selectedMaterialStock?.rate.number}  / ${selectedMaterialStock?.rate.metric} [Material Cost]\n` +
        `${markupObj.value !== 0 ? `+ $${markupObj.formatted} [Mark Up]\n` : ''}` +
        `= $${this.formatted} [Subtotal]`
      );
    }
  };
};

const getSalesTax = (
  subtotalCost: { value: number; formatted: string; tooltip: string },
  tax: number,
  taxExempt: boolean
) => {
  let value = subtotalCost.value * tax;
  if (taxExempt) {
    value = 0;
  }

  value = roundNumber(value);

  return {
    value,
    get formatted() {
      return formatNum(this.value);
    },
    // prettier will try to format this block, but it's already formatted
    // prettier-ignore
    get tooltip() {
      return `$${subtotalCost.formatted} [Subtotal]\n` +
      `x 6% (Michigan sales tax)\n` +
      `= $${this.formatted} [Sales Tax]`;
    }
  };
};

const getHourlyRate = (truck: Truck) => {
  return {
    value: truck.runRate,
    get formatted() {
      return formatNum(this.value);
    },
    get tooltip() {
      return `$${this.formatted} / hr [${truck.name} Run Rate]`;
    }
  };
};

const getTotalMaterial = (
  subtotalCost: { value: number; formatted: string; tooltip: string },
  quantity: number,
  tax: number,
  taxExempt: boolean
) => {
  const taxObj = getSalesTax(subtotalCost, tax, taxExempt);
  const materialWithTax = taxExempt ? subtotalCost.value : subtotalCost.value + taxObj.value;
  const value = materialWithTax;

  return {
    value,
    get formatted() {
      return formatNum(this.value);
    },
    get tooltip() {
      return (
        `$${subtotalCost.formatted} [Subtotal]\n` +
        `+ $${formatNum(taxObj.formatted)} [Sales Tax]\n` +
        `= $${this.formatted} [Total Material]`
      );
    }
  };
};

const getTotalTrips = (
  quantity: number,
  truck: Truck,
  findCapacityUnit: () => 'Volume' | 'Weight',
  quantityMetric: VolumeAndWeightUnits,
  conversionRate: { value: number }
) => {
  let value = quantity / (findCapacityUnit() === 'Volume' ? truck.capacity : truck.weight);
  let isReachingVolume = false;
  let isReachingWeight = false;
  const tonToCy = quantity / conversionRate.value;
  const cyToTon = quantity * conversionRate.value;

  if (quantityMetric === 't') {
    const cyTrips = tonToCy / truck.capacity;
    if (cyTrips > value) {
      value = cyTrips;
      isReachingVolume = true;
    }
  } else {
    const tonTrips = cyToTon / truck.weight;
    if (tonTrips > value) {
      value = tonTrips;
      isReachingWeight = true;
    }
  }

  const decimalPart = (value % 1).toFixed(2);

  const maxWeightTooltip =
    `${quantity} ${quantityMetric} [Qty]\n` +
    `**${formatNum(cyToTon)} t (cy->t = ${conversionRate.value})\n` +
    `/ ${truck.weight} t [${truck.name} Max Weight]\n` +
    `${decimalPart !== zeroDecimals ? `= ${formatNum(value)} [Trips]` : `= ${Math.ceil(value)} [Trips]`}\n` +
    `${decimalPart !== zeroDecimals ? `= ${Math.ceil(cyToTon / truck.weight)} [Trips rounded up]` : ''}`;

  const maxVolumeTooltip =
    `${quantity} ${quantityMetric} [Qty]\n` +
    `**${formatNum(tonToCy)} cy (cy->t = ${conversionRate.value})\n` +
    `/ ${truck.capacity} cy [${truck.name} Max Volume]\n` +
    `${decimalPart !== zeroDecimals ? `= ${formatNum(value)} [Trips]` : `= ${Math.ceil(value)} [Trips]`}\n` +
    `${decimalPart !== zeroDecimals ? `= ${Math.ceil(tonToCy / truck.capacity)} [Trips rounded up]` : ''}`;

  return {
    value,
    get formatted(): string {
      return Math.ceil(this.value).toString();
    },
    get starred(): string {
      return Math.ceil(this.value).toString();
    },
    get tooltip() {
      if (isReachingWeight) {
        return `Reaching Max Weight first\n${maxWeightTooltip}`;
      }
      if (isReachingVolume) {
        return `Reaching Max Volume first\n${maxVolumeTooltip}`;
      }
      return (
        `${quantity} ${quantityMetric} [Qty]\n` +
        `/ ${findCapacityUnit() === 'Volume' ? truck.capacity : truck.weight} ${quantityMetric} [${
          truck.name
        } Max ${findCapacityUnit()}]\n` +
        `${decimalPart !== zeroDecimals ? `= ${formatNum(this.value)} [Trips]` : `= ${this.formatted} [Trips]`}\n` +
        `${decimalPart !== zeroDecimals ? `= ${this.formatted} [Trips rounded up]` : ''}`
      );
    }
  };
};

const getTotalTime = (
  totalJobDuration: number,
  totalTrips: { value: number; formatted: string; starred: string },
  roundTripInHours: string,
  originalTotalTimeInHours: string,
  totalTimeInHours: string,
  minTime: number
) => {
  const originalValue = (totalJobDuration * Math.ceil(totalTrips.value) * 60) / 60;
  let value = originalValue;
  if (value < minTime / 60) {
    value = minTime / 60;
  }
  return {
    value,
    originalValue,
    get formatted() {
      return formatNum(this.value);
    },
    get tooltip() {
      // prettier will try to format this block, but it's already formatted
      // prettier-ignore
      return (
        `${roundTripInHours} [Round Trip]\n` +
        `x ${totalTrips.formatted} [Trips]\n` +
        `= ${originalTotalTimeInHours} [Total Time]\n` +
        `${this.value > this.originalValue ? `< ${this.value * 60} mins [Min Total Time]\n` : ''}` +
        `= ${totalTimeInHours}`
      );
    }
  };
};

const getTotalTruckingCost = (totalTime: { value: number; formatted: string }, truck: Truck, totalTimeInHours: string) => {
  const value = Math.ceil(totalTime.value * 60) * (truck.runRate / 60);
  return {
    value,
    get formatted() {
      return formatNum(this.value);
    },
    get tooltip() {
      return (
        `${totalTimeInHours} [Total Time]\n` +
        `x $${formatNum(truck.runRate)} / hr [Run Rate]\n` +
        `= $${this.formatted} [Trucking]`
      );
    }
  };
};

const getTotalCost = (
  totalMaterial: { value: number; formatted: string },
  totalTruckingCost: { value: number; formatted: string }
) => {
  const value = totalMaterial.value + totalTruckingCost.value;
  return {
    value,
    get formatted() {
      return formatNum(this.value);
    },
    get tooltip() {
      return (
        `$${totalMaterial.formatted} [Total Material]\n` +
        `+ $${totalTruckingCost.formatted} [Trucking]\n` +
        `= $${this.formatted} [Total Cost]`
      );
    }
  };
};

const getQuantity = (quantity: number, quantityMetric: VolumeAndWeightUnits) => {
  return {
    value: quantity,
    get formatted() {
      return this.value;
    },
    get tooltip() {
      return `${this.formatted} ${quantityMetric} [Qty requested]`;
    }
  };
};

const getCostPerLoad = (
  totalCost: { value: number; formatted: string },
  totalTrips: { value: number; formatted: string; starred: string }
) => {
  const value = totalCost.value / Math.ceil(totalTrips.value);
  return {
    value,
    get formatted() {
      return formatNum(this.value);
    },
    get tooltip() {
      // prettier will try to format this block, but it's already formatted
      // prettier-ignore
      return (
            `$${totalCost.formatted} [Total Cost]\n` +
            `/ ${totalTrips.formatted} [Trips]\n` +
            `= $${this.formatted} [Cost/Load]`
        );
    }
  };
};

const getCostPerUnit = (
  totalCost: { value: number; formatted: string },
  quantity: number,
  quantityMetric: VolumeAndWeightUnits
) => {
  const value = totalCost.value / quantity;
  return {
    value,
    get formatted() {
      return formatNum(this.value);
    },
    get tooltip() {
      return (
        `$${totalCost.formatted} [Total Cost]\n` +
        `/ ${quantity} ${quantityMetric} [Qty]\n` +
        `= $${this.formatted} / ${quantityMetric} [Cost / ${quantityMetric}]`
      );
    }
  };
};

const getRoundTripTime = (oneWayTripDuration: number, truck: Truck, roundTripInHours: string) => {
  const multiplier = truck.timeMultiplier || 1;
  const value = Math.ceil(oneWayTripDuration * 2 * multiplier) + truck.unloadLoadTime;
  return {
    value,
    get formatted() {
      return formatNum(this.value);
    },
    get tooltip() {
      return (
        `${formatNum(oneWayTripDuration)} mins (one way trip duration)\n` +
        `x 2\n` +
        `x ${multiplier} trip time mult. (truck setting)\n` +
        `= ${Math.ceil(oneWayTripDuration * 2 * multiplier)} mins (rounded up)\n` +
        `+ ${truck.unloadLoadTime} min load/unload (truck setting)\n` +
        `= ${roundTripInHours} [Round Trip]`
      );
    }
  };
};

export {
  formatNum,
  getMaterialCost,
  getSubtotalCost,
  getTotalMaterial,
  getTotalTrips,
  getTotalTime,
  getTotalTruckingCost,
  getTotalCost,
  getQuantity,
  getMarkup,
  getSalesTax,
  getHourlyRate,
  getCostPerLoad,
  getCostPerUnit,
  getRoundTripTime
};
